import { Outlet, Link, useLocation } from 'react-router';
import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Logo } from '../ui/Logo';

const NAV_LINKS = [
  { href: '/features', label: 'Features' },
  { href: '/security', label: 'Security' },
  { href: '/pricing', label: 'Pricing' },
];

export function MarketingLayout() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-void no-overflow">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-void backdrop-blur-xl border-b border-gold/10">
        <nav className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="group">
              <Logo size="md" />
            </Link>

            {/* Desktop navigation */}
            <div className="hidden md:flex md:items-center md:gap-8">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors relative ${
                    location.pathname === link.href
                      ? 'text-gold'
                      : 'text-stone hover:text-cream'
                  }`}
                >
                  {link.label}
                  {location.pathname === link.href && (
                    <span className="absolute -bottom-1 left-0 right-0 h-px bg-gold" />
                  )}
                </Link>
              ))}
              <div className="flex items-center gap-3 ml-4">
                <Link to="/login">
                  <button className="text-sm text-stone hover:text-cream transition-colors px-4 py-2">
                    Sign In
                  </button>
                </Link>
                <Link to="/request-access">
                  <button className="btn-gold px-5 py-2 rounded-lg text-sm">
                    <span>Request Access</span>
                  </button>
                </Link>
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              className="md:hidden p-2 text-stone hover:text-cream transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-6 border-t border-gold/10">
              <div className="space-y-1">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`block px-4 py-3 rounded-xl text-base transition-colors ${
                      location.pathname === link.href
                        ? 'bg-carbon text-gold'
                        : 'text-stone hover:bg-carbon/50 hover:text-cream'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 space-y-2">
                  <Link
                    to="/login"
                    className="block"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <button className="w-full btn-outline py-3 rounded-xl text-sm">
                      Sign In
                    </button>
                  </Link>
                  <Link
                    to="/request-access"
                    className="block"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <button className="w-full btn-gold py-3 rounded-xl text-sm">
                      <span>Request Access</span>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16" />

      {/* Main content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-gold/10 bg-night">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <Link to="/" className="inline-block mb-6">
                <Logo size="md" />
              </Link>
              <p className="text-sm text-stone max-w-sm leading-relaxed mb-6">
                Enterprise-grade secure file sharing with mandatory two-factor authentication
                and Australian data residency.
              </p>
              <div className="flex items-center gap-2">
                <span className="status-dot" />
                <span className="text-xs text-stone">Sydney Data Center • 99.99% Uptime</span>
              </div>
            </div>

            {/* Links */}
            <div>
              <h3 className="text-sm font-medium text-cream mb-4">Product</h3>
              <ul className="space-y-3">
                {['Features', 'Security', 'Pricing'].map((item) => (
                  <li key={item}>
                    <Link
                      to={`/${item.toLowerCase()}`}
                      className="text-sm text-stone hover:text-gold transition-colors"
                    >
                      {item}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/request-access"
                    className="text-sm text-stone hover:text-gold transition-colors"
                  >
                    Request Access
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-medium text-cream mb-4">Company</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/contact" className="text-sm text-stone hover:text-gold transition-colors">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm text-stone hover:text-gold transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-sm text-stone hover:text-gold transition-colors">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <hr className="hr-gold my-12" />

          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-stone">
              © {new Date().getFullYear()} SecureSend.pro. All rights reserved.
            </p>
            <p className="text-sm text-stone">
              Made in Australia 🇦🇺
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
